// cSpell:ignore betalen forfait Gemeenschappelijk gemeenschappelijk Gemeenschappelijke kosten Privatief privatief Privatieve privatieve terug
// A tenant's balance per kind of cost (Lewis, 2026-10-16): every tenant gets
// a balance per kind, and money never moves between kinds. A provision for
// one kind is reserved for that kind's costs only (by analogy with the rule
// of a co-ownership's reserve). The bottom line stays one total, the net of
// the kinds: the single amount of the tenant's payment request.
//
// - common ("gemeenschappelijk"): the tenant's share of the building's costs
//   minus the common-costs provision;
// - private ("privatief"): the tenant's own (unit) costs minus the private
//   provision: the lease's non-common costs, its extra cost lines and the
//   provisions the broker added by hand.
//
// The extra cost lines of the lease stay in the private kind (Lewis,
// 2026-10-16, final): they can never be linked to specific costs. They are
// listed by their own description inside the private kind, each with its
// share of the private slice (provisionLineRows.utils: the provision history
// is per slice, so a slice is spread over its lines by their monthly
// amounts). The kinds are kept as a list ("extra:<line key>" is a valid key
// that nothing produces today), so a later change would only touch the
// mapping below (costKindOf, provisionLineKindOf), not the structure the
// engine, the snapshot, the API, the screens and the statement share.
//
// A kind the lease prices as a forfait is not settled with that tenant at all
// (Lewis, 2026-10-16, sharpening F35): no costs, no provision, no balance. By
// Belgian law a forfait is never settled; only the justice of the peace can
// revise it. The tenant's share of those costs stays with the owner (it is
// never moved to the other tenants), see forfaitKindsOf and the engine.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import type { TProvisionLineKind } from "./contractProvision.utils";
import { fromCents, toCents } from "./money.utils";
import type { ProvisionSlice } from "./provisionHistory";
import type { ContractRef, InvoiceScope } from "./types";

export const COMMON_KIND = "common";
export const PRIVATE_KIND = "private";
// A kind of its own for an extra cost line of the lease ("extra:additional:2"):
// valid, but not produced (extra lines stay private).
export const EXTRA_KIND_PREFIX = "extra:";

export type TSettlementKindKey =
  | typeof COMMON_KIND
  | typeof PRIVATE_KIND
  | `${typeof EXTRA_KIND_PREFIX}${string}`;

// One provision that comes off a kind's costs.
export interface ISettlementKindProvision {
  // "lease": a provision line of the lease, "added": one the broker added by
  // hand in the provisions step.
  source: "lease" | "added";
  // lease: the line's key on the lease ("common", "nonCommon",
  // "additional:<index>"), or "slice:<slice>" for a slice that counts an
  // amount without a provision line on the lease today; added: its id.
  key: string;
  // lease: the slice of the lease's provision it belongs to, and which line
  // of the contract it is.
  slice?: ProvisionSlice;
  lineKind?: TProvisionLineKind;
  // An extra line's own description on the contract, or the title the broker
  // gave a provision added by hand. Absent otherwise.
  title?: string;
  amount: number;
  // lease: the months its slice counted, possibly part of a month.
  months?: number;
}

// One kind of a tenant's settlement: its costs, the provisions against them
// and what is left.
export interface ISettlementKind {
  key: TSettlementKindKey;
  // An extra kind: the description of its line on the contract.
  title?: string;
  // The tenant's costs of this kind, excl. VAT, and their VAT (per cost at
  // its own rate, as the engine bills it).
  cost: number;
  costVat: number;
  // Every provision of this kind together, and each of them.
  provision: number;
  provisions: ISettlementKindProvision[];
  // cost + costVat − provision, incl. VAT as the payment request bills it:
  // more than 0 the tenant pays, less than 0 they get back.
  balance: number;
}

// The kind a cost is settled in: the building's costs (booked on the group,
// or a common cost) in the common kind, a unit's own cost in the private
// kind. The same rule as the slices of the provision ledger (a group
// settlement reads a cost booked on the group as a common cost, see
// normalizeGroupSettlementCosts), so a group cost that reaches a unit through
// a departure is a common cost there too.
export const costKindOf = (invoice: {
  scope: InvoiceScope;
}): TSettlementKindKey =>
  invoice.scope === "common" ? COMMON_KIND : PRIVATE_KIND;

// The kind a provision line of the lease counts in: the common costs in the
// common kind, the non-common costs and every extra line in the private kind
// (see the top of this file).
export const provisionLineKindOf = (line: {
  slice: ProvisionSlice;
  // The line's key and kind on the lease (see IProvisionLine); absent for a
  // slice without a line.
  lineKey?: string;
  lineKind?: TProvisionLineKind;
}): TSettlementKindKey =>
  line.slice === "common" ? COMMON_KIND : PRIVATE_KIND;

// A provision the broker added by hand counts in the kind they chose for it
// (decision 20, 2026-10-20: "Gemeenschappelijk" or "Privatief"); one added
// before that choice existed counts in the private kind.
export const ADDED_PROVISION_KIND: TSettlementKindKey = PRIVATE_KIND;

export type TAddedProvisionKind = typeof COMMON_KIND | typeof PRIVATE_KIND;

export const addedProvisionKindOf = (extra: {
  kind?: TAddedProvisionKind;
}): TAddedProvisionKind =>
  extra.kind === COMMON_KIND ? COMMON_KIND : PRIVATE_KIND;

// The kinds a lease can price as a forfait: its common-costs field and its
// non-common ("privatieve") costs field. An extra cost line that is a forfait
// is simply ignored: it never makes the private kind a forfait.
export type TFixedFeeKindKey = typeof COMMON_KIND | typeof PRIVATE_KIND;

// The slice of the provision a forfait kind would have settled.
export const sliceOfFixedFeeKind = (key: TFixedFeeKindKey): ProvisionSlice =>
  key === COMMON_KIND ? "common" : "private";

export const isFixedFeeKindKey = (key: unknown): key is TFixedFeeKindKey =>
  key === COMMON_KIND || key === PRIVATE_KIND;

// The kinds of a lease that are not settled because they are a forfait
// (Lewis, 2026-10-16; decision 20 of 2026-10-20): the common kind when the
// common-costs field is a forfait with an amount, the private kind when the
// non-common field is (ContractRef.forfaitCommon / forfaitPrivate). A forfait
// is never settled: neither an extra line of the lease that is a provision
// nor a provision the broker adds by hand makes such a kind one to settle (the
// provisions step does not let the broker add one to a forfait kind).
export const fixedFeeKindsOf = (params: {
  contract: Pick<ContractRef, "fixedFeeCommon" | "fixedFeePrivate">;
}): TFixedFeeKindKey[] => {
  const { contract } = params;
  return [
    ...(contract.fixedFeeCommon ? [COMMON_KIND] : []),
    ...(contract.fixedFeePrivate ? [PRIVATE_KIND] : []),
  ] as TFixedFeeKindKey[];
};

export const isExtraKind = (key: string): boolean =>
  key.startsWith(EXTRA_KIND_PREFIX);

// Common first, then private, then the extra kinds in the order they come.
const kindRank = (key: string): number => {
  if (key === COMMON_KIND) return 0;
  if (key === PRIVATE_KIND) return 1;
  return 2;
};

export const sortKinds = <T extends { key: string }>(kinds: T[]): T[] =>
  kinds
    .map((kind, index) => ({ kind, index }))
    .sort(
      (a, b) =>
        kindRank(a.kind.key) - kindRank(b.kind.key) || a.index - b.index,
    )
    .map(({ kind }) => kind);

// The kinds of one tenant from their costs and provisions per kind, in whole
// cents. A kind with no cost, no VAT and no provision is left out.
export const buildKinds = (params: {
  costCentsByKind: Record<string, number>;
  vatCentsByKind: Record<string, number>;
  provisions: {
    kind: TSettlementKindKey;
    provision: ISettlementKindProvision;
  }[];
  titleOf?: (key: TSettlementKindKey) => string | undefined;
}): ISettlementKind[] => {
  const { costCentsByKind, vatCentsByKind, provisions, titleOf } = params;
  const keys: TSettlementKindKey[] = [];
  const add = (key: TSettlementKindKey) => {
    if (!keys.includes(key)) keys.push(key);
  };
  Object.keys(costCentsByKind).forEach(key => add(key as TSettlementKindKey));
  Object.keys(vatCentsByKind).forEach(key => add(key as TSettlementKindKey));
  provisions.forEach(({ kind }) => add(kind));
  const kinds = keys.flatMap(key => {
    const costCents = costCentsByKind[key] ?? 0;
    const vatCents = vatCentsByKind[key] ?? 0;
    const own = provisions
      .filter(({ kind }) => kind === key)
      .map(({ provision }) => provision);
    const provisionCents = own.reduce(
      (sum, provision) => sum + toCents(provision.amount),
      0,
    );
    if (costCents === 0 && vatCents === 0 && own.length === 0) return [];
    const title = titleOf?.(key);
    return [
      {
        key,
        ...(title ? { title } : {}),
        cost: fromCents(costCents),
        costVat: fromCents(vatCents),
        provision: fromCents(provisionCents),
        provisions: own,
        balance: fromCents(costCents + vatCents - provisionCents),
      },
    ];
  });
  return sortKinds(kinds);
};

// Whether a tenant's kinds add up, to the cent, to their totals: costs, VAT
// and provision. Kinds that do not are never shown (the one balance is).
export const kindsAddUp = (params: {
  kinds: ISettlementKind[];
  cost: number;
  costVat: number;
  provision: number;
}): boolean => {
  const { kinds } = params;
  const sum = (pick: (kind: ISettlementKind) => number) =>
    kinds.reduce((total, kind) => total + toCents(pick(kind)), 0);
  return (
    sum(kind => kind.cost) === toCents(params.cost) &&
    sum(kind => kind.costVat) === toCents(params.costVat) &&
    sum(kind => kind.provision) === toCents(params.provision) &&
    kinds.every(
      kind =>
        toCents(kind.balance) ===
          toCents(kind.cost) +
            toCents(kind.costVat) -
            toCents(kind.provision) &&
        toCents(kind.provision) ===
          kind.provisions.reduce(
            (total, provision) => total + toCents(provision.amount),
            0,
          ),
    )
  );
};

// The kinds a tenant's card and statement show apart: when there are two or
// more, or when a kind has more than one provision (an extra line of the
// lease, or one added by hand, next to the slice's own line: it shows that
// they count in the private kind), and only when they add up to the
// tenant's totals. One kind with one provision is the total itself, and
// kinds frozen before this existed are absent.
export const shownKinds = (pc: {
  kinds?: ISettlementKind[];
  cost: number;
  costVat?: number;
  provision: number;
}): ISettlementKind[] | undefined => {
  const { kinds } = pc;
  if (!kinds || pc.costVat == null) return undefined;
  if (kinds.length < 2 && !kinds.some(kind => kind.provisions.length > 1)) {
    return undefined;
  }
  return kindsAddUp({
    kinds,
    cost: pc.cost,
    costVat: pc.costVat,
    provision: pc.provision,
  })
    ? kinds
    : undefined;
};

// ── Labels, as a tenant's card names its kinds ──────────────────────────────

// "Gemeenschappelijke kosten", "Privatieve kosten", or an extra kind's line.
export const kindLabel = (
  kind: Pick<ISettlementKind, "key" | "title">,
): string => {
  if (kind.key === COMMON_KIND) {
    return getLocalizedText("settlement.summary.kind.common");
  }
  if (kind.key === PRIVATE_KIND) {
    return getLocalizedText("settlement.summary.kind.private");
  }
  return (
    kind.title?.trim() ||
    getLocalizedText("settlement.provisions.line.additional_fallback")
  );
};

// What is left of a kind: "€ 240,00 bij te betalen", "€ 22,00 terug", or
// "€ 0,00".
export const kindBalanceText = (
  balance: number,
  formatAmount: (amount: number) => string,
): string => {
  const balanceCents = toCents(balance);
  if (balanceCents > 0) {
    return getLocalizedText("settlement.summary.kind.to_pay", {
      amount: formatAmount(balance),
    });
  }
  if (balanceCents < 0) {
    return getLocalizedText("settlement.summary.kind.to_receive", {
      amount: formatAmount(-balance),
    });
  }
  return formatAmount(0);
};
